import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from './AuthContext';
import type { OnboardingStatus, OnboardingStep } from '@shared/types/onboarding';

type Ctx = {
  status: OnboardingStatus | null;
  loading: boolean;
  saveStep: (step: OnboardingStep, data: any) => Promise<void>;
  complete: (finalData: any) => Promise<void>;
  refresh: () => Promise<void>;
};

const OnboardingContext = createContext<Ctx | null>(null);

async function api<T = any>(method: string, path: string, body?: any): Promise<T> {
  const res = await apiRequest(method, `/api/v1/onboarding${path}`, body);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser, currentTenant, loading: authLoading } = useAuth();

  const refresh = useCallback(async () => {
    // Only fetch onboarding status if user is authenticated and has a tenant
    if (!currentUser || !currentTenant) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await api<OnboardingStatus>('GET', '/status');
      setStatus(data);
    } catch (error: any) {
      console.error('❌ Failed to fetch onboarding status:', error);
      // If we get a 401 or 428 error, it means onboarding is required
      if (error.message?.includes('401') || error.message?.includes('428')) {
        // Set a default status indicating onboarding is required
        setStatus({
          onboarding_completed: false,
          onboarding_step: 'business_info',
          onboarding_data: {}
        });
      } else {
        console.error('Failed to fetch onboarding status:', error);
      }
    } finally { 
      setLoading(false); 
    }
  }, [currentUser, currentTenant]);

  useEffect(() => { 
    // Wait for authentication to complete before fetching onboarding status
    if (authLoading) {
      return;
    }
    
    if (currentUser && currentTenant) {
      void refresh(); 
    } else {
      setLoading(false);
    }
  }, [refresh, currentUser, currentTenant, authLoading]);

  const saveStep = useCallback(async (step: OnboardingStep, data: any) => {
    try {
      let path = '';
      let payload = data;
      
      switch (step) {
        case 'business_info':
          path = '/shop-settings';
          payload = {
            businessName: data.businessName,
            businessType: data.businessType,
            shopSettings: {
              name: data.shopName,
              phone: data.phone,
              email: data.email,
              addressInfo: data.address,
              // Persist required settings
              defaultShopRate: typeof data.defaultShopRate === 'number' ? data.defaultShopRate : Number(data.defaultShopRate ?? 0),
              taxRate: typeof data.taxRate === 'number' ? data.taxRate : Number(data.taxRate ?? 0),
              currency: data.currency,
              shopHours: data.shopHours,
            }
          };
          break;
        case 'service_config':
          // First, set defaults based on businessType
          await api('POST', '/service-config', { businessType: data.businessType });
          // Then, persist selected templates if any
          if (Array.isArray(data.selectedTemplates) && data.selectedTemplates.length > 0) {
            await api('POST', '/templates', { selectedTemplateIds: data.selectedTemplates });
          }
          // Save the service config data to onboarding_data
          await api('POST', '/service-config-data', data);
          // Refresh to get updated server state
          await refresh();
          return;
        case 'inventory':
          path = '/inventory';
          payload = {
            defaultCategories: data.categories,
            defaultAssetTypes: data.assetTypes,
          };
          break;
        default:
          path = '/status';
      }
      
      await api('POST', path, payload);
      // Always refresh after saving to get updated server state
      await refresh();
    } catch (error) {
      console.error('Failed to save step:', error);
      throw error;
    }
  }, [refresh]);

  const complete = useCallback(async (finalData: any) => {
    await api('POST', '/complete', finalData);
    await refresh();
  }, [refresh]);

  return (
    <OnboardingContext.Provider value={{ status, loading, saveStep, complete, refresh }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
}


